import {reactFetch} from '@github-ui/verified-fetch'
import {Banner, Link} from '@primer/react'
import {useRef, useState} from 'react'

import {restoreFocusAfterDismiss} from '../../restore-focus-after-dismiss'
import type {AccountTwoFactorRequirementBannerPayload} from './types'

const TWO_FACTOR_REQUIREMENT_BLOG_URL =
  'https://github.blog/2023-03-09-raising-the-bar-for-software-security-github-2fa-begins-march-13/'

const longDateFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'long',
  timeZone: 'UTC',
})

interface AccountTwoFactorRequirementBannerProps extends AccountTwoFactorRequirementBannerPayload {
  returnFocusIds?: string[]
}

export function AccountTwoFactorRequirementBanner({
  state,
  requiredBy,
  dismissUrl,
  primaryAction,
  returnFocusIds,
}: AccountTwoFactorRequirementBannerProps) {
  const [dismissed, setDismissed] = useState(false)
  const [primaryActionLoading, setPrimaryActionLoading] = useState(false)
  const bannerRef = useRef<HTMLElement>(null)
  const hasTwoFactorAuthentication = state === 'enabled'
  const enforceDate = longDateFormatter.format(new Date(requiredBy))

  const dismiss = async () => {
    try {
      const response = await reactFetch(dismissUrl, {method: 'PATCH'})
      if (response.ok) {
        restoreFocusAfterDismiss(bannerRef.current, returnFocusIds)
        setDismissed(true)
      }
    } catch {
      // Keep the banner visible so the viewer can retry.
    }
  }

  const handleOnClick = async () => {
    setPrimaryActionLoading(true)
    try {
      const response = await reactFetch(primaryAction.url, {method: 'PATCH'})
      if (response.ok) {
        if (hasTwoFactorAuthentication) {
          window.location.assign(response.url)
        } else {
          const {redirectUrl} = (await response.json()) as {redirectUrl: string}
          window.location.assign(redirectUrl)
        }
      }
    } catch {
      // Keep the action available so the viewer can retry.
    }
    setPrimaryActionLoading(false)
  }

  if (dismissed) return null

  return (
    <Banner
      ref={bannerRef}
      variant={hasTwoFactorAuthentication ? 'info' : 'warning'}
      title="Two-factor authentication requirement"
      hideTitle
      description={
        <>
          GitHub users are{' '}
          <Link inline href={TWO_FACTOR_REQUIREMENT_BLOG_URL} target="_blank" rel="noopener noreferrer">
            now required
          </Link>{' '}
          to enable two-factor authentication as an additional security measure. Your activity on GitHub includes you in
          this requirement.{' '}
          {hasTwoFactorAuthentication
            ? `No action is required on your part, but two-factor authentication will be permanently required on your account after ${enforceDate}.`
            : `You will need to enable two-factor authentication on your account before ${enforceDate}, or be restricted from account actions.`}
        </>
      }
      onDismiss={dismiss}
      flush
      primaryAction={
        <Banner.PrimaryAction loading={primaryActionLoading} onClick={handleOnClick}>
          {hasTwoFactorAuthentication ? 'Manage 2FA' : 'Enable 2FA'}
        </Banner.PrimaryAction>
      }
    />
  )
}
